/* eslint-disable @typescript-eslint/no-explicit-any */
import { Span, SpanStatusCode, trace } from "@opentelemetry/api";
import { getWebAutoInstrumentations } from "@opentelemetry/auto-instrumentations-web";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from "@opentelemetry/core";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { resourceFromAttributes } from "@opentelemetry/resources";
//exporters
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";
import { clientOnly$ } from "vite-env-only/macros";

import { getEnv } from "./utils/environment";

const environement =
  clientOnly$(getEnv(window.location.href) ?? "undefined") ?? "undefined";

const traceExporter = new OTLPTraceExporter({
  url: `https://monitoring.tools.hellocarbo.com/otel/v1/traces`,
  headers: {
    "X-Api-Key": `${import.meta.env.VITE_OTEL_COLLECTOR_TOKEN}`,
    "Content-Type": "application/json",
  },
});

const tracesProvider = new WebTracerProvider({
  resource: resourceFromAttributes({
    [ATTR_SERVICE_NAME]: `bolo-client-ui-${environement}`,
  }),
  // Note: For production consider using the "BatchSpanProcessor" to reduce the number of requests
  // to your exporter. Using the SimpleSpanProcessor here as it sends the spans immediately to the
  // exporter without delay
  spanProcessors: [new BatchSpanProcessor(traceExporter)],
});

// ZoneContextManager is a context manager implementation based on the Zone.js library. It enables context propagation within the application using zones.
tracesProvider.register({
  contextManager: new ZoneContextManager(),
  // Configure the propagator to enable context propagation between services using the W3C Trace Headers
  propagator: new CompositePropagator({
    propagators: [new W3CBaggagePropagator(), new W3CTraceContextPropagator()],
  }),
});

trace.setGlobalTracerProvider(tracesProvider);

const getPathname = (span: Span) => {
  const attributes = (span as any).attributes;
  const url = new URL(attributes["http.url"]);

  return url.pathname;
};

const startOtelInstrumentation = () => {
  // Registering instrumentations
  registerInstrumentations({
    tracerProvider: tracesProvider,
    instrumentations: [
      getWebAutoInstrumentations({
        "@opentelemetry/instrumentation-xml-http-request": {
          enabled: true,
          clearTimingResources: true,
          // propagate the "traceparent" header to all requests except for specified domains (eg. third party services)
          propagateTraceHeaderCorsUrls: [
            /https?:\/\/(?![\w.-]+\.(?:hubspot\.com|segment\.io|linkedin\.com)[^\w.-])\S*/g,
          ],
        },
        "@opentelemetry/instrumentation-document-load": {
          enabled: true,
          applyCustomAttributesOnSpan: {
            documentLoad: (span) => {
              const attributes = (span as any).attributes;
              attributes["http.method"] = "GET";
              span.updateName(
                `DL: ${attributes["http.method"]} ${getPathname(span)}`
              );
            },
            documentFetch: (span) => {
              span.updateName(`DF: ${getPathname(span)}`);
            },
            resourceFetch: (span) => {
              span.updateName(`RF: ${getPathname(span)}`);
            },
          },
        },
        "@opentelemetry/instrumentation-user-interaction": {
          enabled: true,
          eventNames: [
            "click",
            "load",
            "loadeddata",
            "loadedmetadata",
            "loadstart",
            "error",
          ],
          shouldPreventSpanCreation: (eventType, element, span) => {
            const traceable = element.getAttribute("traceable");
            if (!traceable || traceable == "false") {
              // will not record this span
              return true;
            }
            span.updateName(`click ${element.id}`);
            span.setAttribute("target.id", element.id);
            span.setAttribute(
              "target.title",
              element.title || element.innerText
            );
            // will record this span
            return false;
          },
        },
        "@opentelemetry/instrumentation-fetch": {
          enabled: true,
          propagateTraceHeaderCorsUrls: [/.+/g],
          applyCustomAttributesOnSpan: (span, request, result) => {
            const attributes = (span as any).attributes;
            if (attributes.component === "fetch") {
              span.updateName(
                `${attributes["http.method"]} ${getPathname(span)}`
              );
            }
            if (result.status && result.status > 399) {
              span.setStatus({ code: SpanStatusCode.ERROR });
            } else {
              span.setStatus({ code: SpanStatusCode.OK });
            }
          },
        },
      }),
    ],
  });
};

export { startOtelInstrumentation };
